<template>
  <div class="relative flex flex-col clamp-gap-4-6">
    <div
      :class="[{ 'justify-center': shouldCenterCards }]"
      class="scroll-snap-container overflow-x-scroll flex justify-start hide-scrollbars"
      ref="containerRef"
      @scroll="updateArrowVisibility">
      <div
        class="cardsWrapper flex flex-row items-center justify-center page-px clamp-gap-4-6"
        ref="wrapperRef">
        <SbElementCardsCard
          v-if="cardWidth"
          v-for="(card, index) in cards"
          :key="index"
          :blok="card"
          :sizes="[
            { bpMin: 'ds', vw: 20 },
            { bpMin: 'tp', vw: 25 },
            { bpMin: '0', vw: 50 },
          ]"
          :roundedMedia="roundedMedia"
          :isCarousel="true"
          ref="cardRef"
          class="scroll-snap-item w-[--width]"
          :style="{
            '--width': cardWidth + 'px',
          }" />
      </div>
    </div>
    <AIconButton
      v-if="!shouldCenterCards"
      icon="arrow-left"
      size="sm"
      ariaLabel="Go to previous"
      :disabled="!showLeftArrow"
      class="!absolute top-50% left-0 translate-y--50% clamp-mx-3-5 z-100"
      @click="scrollCarousel('left')" />
    <AIconButton
      v-if="!shouldCenterCards"
      icon="arrow-right"
      size="sm"
      ariaLabel="Go to next"
      :disabled="!showRightArrow"
      class="!absolute top-50% right-0 translate-y--50% clamp-mx-3-5 z-100"
      @click="scrollCarousel('right')" />
  </div>
</template>

<script setup lang="ts">
import { ElementCardsCardStoryblok } from "@/types";
import { useWindowSize } from "@vueuse/core";
import { getBreakpoint } from "@/../uno-preset/preset-theme";

interface Props {
  cards: ElementCardsCardStoryblok[];
  roundedMedia?: boolean;
}

const props = defineProps<Props>();

const { width: windowWidth } = useWindowSize();

const containerRef = ref<HTMLElement | null>(null);
const wrapperRef = ref<HTMLElement | null>(null);
const cardRef = ref<HTMLElement | null>(null);

const cardWidth = ref(0);
const gapWidth = ref(0);

const currentPage = ref(1);

const scrollDistance = ref(0);
const showLeftArrow = ref(false);
const showRightArrow = ref(true);
const scrollLeft = ref(0);

watch(windowWidth, (newVal) => {
  calculateCardWidth(newVal);
});

const cardsPerScreen = computed(() => {
  if (windowWidth.value < getBreakpoint("mpm")) return 1;
  else if (windowWidth.value < getBreakpoint("tp")) return 2;
  else if (windowWidth.value < getBreakpoint("tl")) return 3;
  else if (windowWidth.value < getBreakpoint("dl")) return 4;
  else return 5;
});

const calculateCardWidth = (_windowWidth: number) => {
  if (!wrapperRef.value) return;
  const compStyles = window.getComputedStyle(wrapperRef.value);
  const marginLeft = parseFloat(compStyles.marginLeft);

  gapWidth.value = parseFloat(compStyles.gap);
  cardWidth.value =
    (_windowWidth -
      (marginLeft * 2 + gapWidth.value * (cardsPerScreen.value - 1))) /
    cardsPerScreen.value;
};

const updateCurrentPage = (page: number) => {
  currentPage.value = page;
};

const scrollCarousel = (direction: "left" | "right") => {
  if (containerRef.value) {
    scrollDistance.value = Math.ceil(cardWidth.value + gapWidth.value);
    if (direction === "left") {
      scrollLeft.value = containerRef.value.scrollLeft - scrollDistance.value;
      containerRef.value.scrollTo({
        left: scrollLeft.value,
        behavior: "smooth",
      });

      updateCurrentPage(currentPage.value - 1);
    } else {
      scrollLeft.value = containerRef.value.scrollLeft + scrollDistance.value;
      containerRef.value.scrollTo({
        left: scrollLeft.value,
        behavior: "smooth",
      });
      updateCurrentPage(currentPage.value + 1);
    }
  }
};

watch(scrollLeft, (newVal) => {
  if (!containerRef.value) return;
  showLeftArrow.value = newVal > 0;
  showRightArrow.value =
    newVal + containerRef.value.clientWidth <
    containerRef.value.scrollWidth - 1;
});

const updateArrowVisibility = () => {
  if (!containerRef.value) return;

  const containerWidth = containerRef.value.clientWidth;
  const scrollWidth = containerRef.value.scrollWidth;
  scrollLeft.value = containerRef.value.scrollLeft;

  // If scroll position is at the beginning, hide the left arrow
  if (scrollLeft.value === 0) {
    showLeftArrow.value = false;
  } else {
    showLeftArrow.value = true;
  }

  // If scroll position is at the end, hide the right arrow
  if (scrollLeft.value + containerWidth >= scrollWidth) {
    showRightArrow.value = false;
  } else {
    showRightArrow.value = true;
  }
};

const shouldCenterCards = computed(() => {
  return props.cards.length < cardsPerScreen.value;
});

onMounted(() => {
  calculateCardWidth(windowWidth.value);
  updateCurrentPage(currentPage.value);
});
</script>

<style scoped lang="scss">
.scroll-snap-container {
  scroll-snap-type: x mandatory;
}
.scroll-snap-item {
  scroll-snap-align: center;
}
</style>
